import React, { useState } from "react"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { round100, TTCtoHT } from "../../../utilities/Addons"
import { selectTarif } from "../../../hooks/useTarif"
import CommandeHeader from "./CommandeHeader"

// Prix : toujours 2 décimales (laisse la valeur telle quelle si non numérique).
const fmt2 = (v) => {
	const n = Number(v)
	return Number.isNaN(n) ? v : n.toFixed(2)
}

// Carte d'une commande (non virtuelle) : en-tête fournisseur + date + total,
// et tableau d'articles repliable. Affichage seul (comme l'ancien onglet).
// Prix affichés en HT (le flag prixAchatHT dit si le prix stocké l'est déjà).
const CommandeCard = ({ commande }) => {
	const [open, setOpen] = useState(true)
	const tva = useSelector((s) => selectTarif(s).tva)
	const articles = commande.articles || []
	const prixHT = (a) =>
		a.prixAchatHT ? a.prixAchat : TTCtoHT(a.prixAchat, tva)
	const total = round100(
		articles.reduce((s, a) => s + a.quantite * prixHT(a), 0),
	)

	return (
		<div style={cs.card}>
			<CommandeHeader
				fournisseur={commande.fournisseurNom}
				date={commande.datePrevue}
				total={total}
				totalLabel="CHF HT"
				actions={
					<button
						type="button"
						style={cs.toggle}
						onClick={() => setOpen((o) => !o)}
						title={open ? "Replier" : "Déplier"}
					>
						<FontAwesomeIcon
							icon={open ? faChevronUp : faChevronDown}
						/>
					</button>
				}
			/>

			{open && (
				<div style={cs.tableWrap}>
					<table style={cs.table}>
						{/* Largeurs fixes → colonnes alignées sur toutes les cartes */}
						<colgroup>
							<col style={{ width: "7%" }} />
							<col style={{ width: "28%" }} />
							<col style={{ width: "22%" }} />
							<col style={{ width: "13%" }} />
							<col style={{ width: "15%" }} />
							<col style={{ width: "15%" }} />
						</colgroup>
						<thead>
							<tr>
								<th style={cs.th}>N°</th>
								<th style={cs.th}>Article</th>
								<th style={cs.th}>Catégorie</th>
								<th style={cs.th}>Quantité</th>
								<th style={cs.th}>Qté restante</th>
								<th style={cs.thRight}>Prix achat HT</th>
							</tr>
						</thead>
						<tbody>
							{articles.map((a, i) => (
								<tr
									key={`${a.id}-${i}`}
									style={{
										backgroundColor:
											i % 2 === 1
												? COLORS.bg
												: COLORS.surface,
									}}
								>
									<td style={cs.td} title={String(i + 1)}>
										{i + 1}
									</td>
									<td
										style={{ ...cs.td, fontWeight: 600 }}
										title={a.articleNom}
									>
										{a.articleNom}
									</td>
									<td style={cs.td} title={a.categorieNom}>
										{a.categorieNom}
									</td>
									<td
										style={cs.td}
										title={String(a.quantite)}
									>
										{a.quantite}
									</td>
									<td
										style={cs.td}
										title={String(a.quantite_restante)}
									>
										{a.quantite_restante}
									</td>
									<td
										style={cs.tdRight}
										title={fmt2(prixHT(a))}
									>
										{fmt2(prixHT(a))}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

const cs = {
	card: {
		backgroundColor: COLORS.surface,
		borderRadius: 14,
		boxShadow:
			"0 1px 2px rgba(28, 25, 23, 0.04), 0 6px 20px rgba(28, 25, 23, 0.06)",
		marginBottom: 16,
		overflow: "hidden",
	},
	toggle: {
		width: 32,
		height: 32,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		cursor: "pointer",
		fontSize: 13,
	},
	tableWrap: { overflowX: "auto" },
	table: {
		width: "100%",
		minWidth: 640,
		tableLayout: "fixed",
		borderCollapse: "separate",
		borderSpacing: 0,
		fontSize: 13,
	},
	th: {
		padding: "10px 16px",
		textAlign: "left",
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		border: "none",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	thRight: {
		padding: "10px 16px",
		textAlign: "right",
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		border: "none",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		whiteSpace: "nowrap",
	},
	td: {
		padding: "10px 16px",
		border: "none",
		textAlign: "left",
		color: COLORS.text,
		verticalAlign: "middle",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	tdRight: {
		padding: "10px 16px",
		border: "none",
		textAlign: "right",
		color: COLORS.text,
		verticalAlign: "middle",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontVariantNumeric: "tabular-nums",
	},
}

export default CommandeCard
